import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { EntityBrowserComponent } from '../../../../../../common-modules/components/components/entity-browser/entity-browser.component';
import { OrganizationsService } from '../../../../organizations/services/organizations.service';
import { AuthService } from '../../../../auth/services/auth.service';
import { QuestionnaireReview } from '../../../interfaces/questions.interface';
import { QuestionnaireRequiredAnswers } from '../../../enums/cifc-questionnaire-required-answers.enum';
import { QuestionnaireStatus, QuestionnaireStatusEnum } from '../../../enums/cifc-questionnaire-status.enum';
import { CifcService } from '../../../services/cifc.service';
import { CstQuestionnaireService } from '../../../services/cst-questionnaire.service';
import { CommonQuestionnaireService } from '../../../services/common-questionnaire.service';
import { Router } from '@angular/router';
import { CustomerQuestionnaireService } from '../../../services/customer-questionnaire.service';
import { interval, Subscription, take } from 'rxjs';
import { CompaniesService } from '../../../../companies/services/companies.service';
import { IdentityService } from '../../../../auth/services/identity.service';
import { TimeframeService } from '../../../../timeframe/services/timeframe.service';

@Component({
    selector: 'app-cifc-view',
    templateUrl: './cifc-view.component.html',
    styleUrls: ['./cifc-view.component.scss'],
    standalone: false,
})
export class CifcViewComponent extends EntityBrowserComponent<QuestionnaireReview> implements AfterViewInit, OnDestroy, OnInit {
    /** displayedColumns -columns that will be displayed on the cifc table */
    public displayedColumns: string[] = ['company', 'status', 'required_answers', 'updatedAt', 'answered_questions', 'progress'];
    /**
     * QuestionnaireStatus - used for populating the status column
     */
    public QuestionnaireStatus = new QuestionnaireStatus();

    /**  QuestionnaireFirstSubmitted - used for populating the first submitted column */
    public QuestionnaireRequiredAnswers = new QuestionnaireRequiredAnswers();
    public publishInProgress: boolean = false;
    public loadQuestionnaireSub: Subscription;

    constructor(
        public authService: AuthService,
        public companiesService: CompaniesService,
        public identityService: IdentityService,
        protected organizationsService: OrganizationsService,
        protected changeDetector: ChangeDetectorRef,
        protected cstQuestionnaireService: CstQuestionnaireService,
        protected customerQuestionnaireService: CustomerQuestionnaireService,
        protected commonQuestionnaireService: CommonQuestionnaireService,
        protected cifcService: CifcService,
        protected router: Router,
        timeframeService: TimeframeService,
    ) {
        super(cifcService, organizationsService, companiesService, identityService, authService, changeDetector, timeframeService, router);
        this.deleteCompanyFilter = false;
        this.ignoreCompanyFilter = true;
    }

    public get CompanyFilter(): string {
        return this.filter.company;
    }

    public set CompanyFilter(value: string) {
        this.setFilterField('company', value);
    }

    public get StatusFilter(): string {
        return this.filter.status ? this.filter.status : '';
    }

    public set StatusFilter(value: string) {
        this.setFilterField('status', value);
    }

    public get RequiredAnswersFilter(): string {
        return this.filter.required_answers ? this.filter.required_answers : '';
    }

    public set RequiredAnswersFilter(value: string) {
        this.setFilterField('required_answers', value);
    }

    public get QuestionsAnsweredFilter(): string {
        return this.filter.questions_answered;
    }

    public set QuestionsAnsweredFilter(value: string) {
        this.setFilterField('questions_answered', value);
    }

    public get LastUpdateStartFilter(): string {
        return this.filter.last_update_start;
    }

    public set LastUpdateStartFilter(value: string) {
        const newValue = value ? new Date(value).toISOString() : value;
        this.setFilterField('last_update_start', newValue);
    }

    public get ProgressFilter(): string {
        return this.filter.progress_filter;
    }

    public set ProgressFilter(value: string) {
        this.setFilterField('progress_filter', value);
    }

    public get LastUpdateEndFilter(): string {
        const computedDate = this.filter.last_update_end;

        if (!computedDate) {
            /* empty date */
            return computedDate;
        }

        /* get the computed version of the date (23:59 time) */
        const endOfDay = new Date(computedDate);

        /* increment with one millisecond */
        const date = new Date(endOfDay.valueOf() + 1);

        /* subtract one day (24 hours) to get the input structure (00:00 time) */
        date.setDate(date.getDate() - 1);

        return date.toISOString();
    }

    public set LastUpdateEndFilter(value: string) {
        if (value) {
            /* get original date from input (00:00 time) */
            const date = new Date(value);

            /* increment with one day (24 hours) */
            date.setDate(date.getDate() + 1);

            /* subtract one millisecond */
            const endOfDay = new Date(date.valueOf() - 1);

            this.setFilterField('last_update_end', endOfDay.toISOString());
        } else {
            this.setFilterField('last_update_end', value);
        }
    }

    ngOnInit(): void {
        this.entityDataSource.connect().subscribe((response) => {});
        interval(5000)
            .pipe(take(10))
            .subscribe((val) => {
                this.loadQuestionnaireSub = this.customerQuestionnaireService.loadAllQuestionnaires().subscribe((questionnaires) => {
                    this.commonQuestionnaireService.isPublishInProgress = questionnaires[0].publish_in_progress;
                });
            });
    }

    ngAfterViewInit(): void {
        super.ngAfterViewInit();
    }

    ngOnDestroy() {
        super.ngOnDestroy();
        this.loadQuestionnaireSub?.unsubscribe();
    }

    /* Clear all table filters */
    public clearFilters() {
        super.clearFilters();
        this.CompanyFilter = '';
        this.ProgressFilter = '';
        this.StatusFilter = '';
        this.LastUpdateStartFilter = '';
        this.LastUpdateEndFilter = '';
        this.RequiredAnswersFilter = '';
        this.QuestionsAnsweredFilter = '';
    }

    public onCompanyFilterChanged(filter: string) {
        this.CompanyFilter = filter;
        this.entityDataSource.Data = this.entityDataSource.Data.filter((data) =>
            data.company_data.cname.toLowerCase().includes(filter.toLowerCase()),
        );
    }

    public getCompanyName(questionnaire: QuestionnaireReview): string {
        return questionnaire.company_data?.cname ?? '';
    }

    public enableCreateMode() {
        this.commonQuestionnaireService.createMode = true;
        this.commonQuestionnaireService.reviewMode = false;
    }

    public async goToCustomerReview(questionnaire: QuestionnaireReview) {
        if (!this.isCompanyQuestionnaireDisabled(questionnaire)) {
            await this.router.navigate(['/cq/questionnaire/' + questionnaire.company_data.cid]);
        }
    }

    public isCompanyQuestionnaireDisabled(questionnaire: QuestionnaireReview) {
        return questionnaire.answered_questions === 0 || questionnaire.status === QuestionnaireStatusEnum.AddingReviews;
    }
}
